import { useState } from 'react';
import { TRACKER_IDS, TRACKER_NAMES, type Payload, type TrackerId, type WindowKey } from '../api/types';
import { chainName, pct, spreadOf, usd, gapStep } from '../lib/format';
import { finite } from '../ui/charts';
import { CardHead, GlassColumns, GlassHBars } from '../ui/glass';

type Mode = 'share' | 'usd';

export function Chains({ data, win }: { data: Payload; win: WindowKey }) {
  const [mode, setMode] = useState<Mode>('share');
  const [focus, setFocus] = useState<TrackerId | null>(null);

  // The chain split is only published over seven days. Any other window falls
  // back to it, and the page says so rather than showing an empty chart.
  const own = data.analysis.volume[win];
  const src = own?.chains && Object.keys(own.chains).length ? own : data.analysis.volume['7d'];
  const fellBack = src !== own;
  const table = src?.chains ?? {};
  const codes = Object.keys(table);
  if (!codes.length) return <div className="tile"><p className="empty">No tracker publishes a chain split in this collection.</p></div>;

  const trackers = TRACKER_IDS.filter((id) => codes.some((c) => finite(table[c]?.[id])));
  const totals = Object.fromEntries(trackers.map((id) => [id, codes.reduce((s, c) => s + (table[c]?.[id] ?? 0), 0)])) as Record<TrackerId, number>;
  const share = (c: string, id: TrackerId) => {
    const v = table[c]?.[id];
    return finite(v) && totals[id] ? (v / totals[id]) * 100 : null;
  };
  const chains = codes
    .map((c) => ({ code: c, name: chainName(c), top: Math.max(...trackers.map((id) => share(c, id) ?? 0)) }))
    .sort((x, y) => y.top - x.top);
  const lead = chains[0];
  const focusId = focus && trackers.includes(focus) ? focus : trackers[0];

  return (
    <div className="page">
      <section className="tile banner-head banner-chains">
        <div className="page-head">
          <div>
            <h1>Blockchains</h1>
            <p>Where each tracker says the deposits arrive. Shown as a share of each tracker’s own total, so a tracker that follows more casinos does not look bigger on every chain.</p>
          </div>
          <div className="seg" role="group" aria-label="Unit">
            <button aria-pressed={mode === 'share'} onClick={() => setMode('share')}>Share</button>
            <button aria-pressed={mode === 'usd'} onClick={() => setMode('usd')}>USD</button>
          </div>
        </div>
        {fellBack && <div className="banner"><span>The chain split is published for <b>7 days</b> only, so this page shows the 7-day window.</span></div>}
        <div className="kpis">
          <div className="kpi"><small>Chains tracked</small><b>{codes.length}</b><span>across {trackers.length} trackers</span></div>
          {lead && <div className="kpi"><small>Busiest chain</small><b>{lead.name}</b><span>up to {pct(lead.top, 0)} of a tracker’s deposits</span></div>}
          {trackers.slice(0, 2).map((id) => (
            <div key={id} className="kpi"><small><i className="swatch" style={{ background: `var(--tracker-${id})` }} aria-hidden />{TRACKER_NAMES[id]}</small><b>{usd(totals[id])}</b><span>total over the shared list</span></div>
          ))}
        </div>
      </section>

      <div className="pgrid">
        <section className="tile span-7">
          <CardHead icon="chain" tone="teal" title="Chain by chain" note={mode === 'share' ? 'Share of each tracker’s deposits, largest chains first' : 'Deposits, USD, largest chains first'} />
          <GlassColumns
            groups={chains.slice(0, 8).map((c) => c.name)}
            series={trackers.map((id) => ({
              key: id, name: TRACKER_NAMES[id], color: `var(--tracker-${id})`,
              values: chains.slice(0, 8).map((c) => (mode === 'share' ? share(c.code, id) : table[c.code]?.[id] ?? null)),
            }))}
            format={(v) => (mode === 'share' ? pct(v) : usd(v))}
            height={200}
          />
        </section>
        <section className="tile span-5">
          <CardHead icon="steps" tone="blue" title="One tracker’s mix" note="Where its deposits arrive">
            <div className="seg seg-sm" role="group" aria-label="Tracker">
              {trackers.map((id) => <button key={id} aria-pressed={id === focusId} onClick={() => setFocus(id)}>{TRACKER_NAMES[id]}</button>)}
            </div>
          </CardHead>
          {focusId && (
            <GlassHBars max={100} items={chains.slice(0, 8).flatMap((c) => {
              const s = share(c.code, focusId);
              return finite(s) ? [{ key: c.code, label: c.name, value: s, display: pct(s), color: `var(--tracker-${focusId})` }] : [];
            })} />
          )}
        </section>
      </div>

      <section className="tile">
        <div className="tile-head"><h2 className="tile-title">Every chain</h2><span className="tile-note">apart = how far the trackers’ shares differ</span></div>
        <div className="t-wrap">
          <table className="t">
            <thead>
              <tr>
                <th>Chain</th>
                {trackers.map((id) => <th key={id} className="r"><i className="swatch" style={{ background: `var(--tracker-${id})` }} aria-hidden /> {TRACKER_NAMES[id]}</th>)}
                <th className="r">Apart</th>
              </tr>
            </thead>
            <tbody>
              {chains.map((c) => {
                const shares = trackers.map((id) => share(c.code, id));
                const apart = spreadOf(shares);
                return (
                  <tr key={c.code}>
                    <td><b>{c.name}</b></td>
                    {trackers.map((id, i) => {
                      const v = table[c.code]?.[id];
                      return <td key={id} className="r">{finite(v) ? <>{usd(v)} <small className="muted">{pct(shares[i], 0)}</small></> : <span className="muted">—</span>}</td>;
                    })}
                    <td className="r">{finite(apart) ? <span className={`gap-pill g${gapStep(apart)}`}>{pct(apart, 0)}</span> : <span className="muted">—</span>}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
